// ─── Kick-off do contrato (wizard) ──────────────────────────────────────────
// Onda 4 (2026-06): modal simples que só pedia a data de assinatura.
// 2026-06-22: 3 modos de vínculo —
//   • Contrato novo (default): cria contrato + serviços + parcelas.
//   • Aditivo de existente: cria aditivo HY…-A{n} herdando do pai; gera serviços/parcelas.
//   • Vincular a existente: SÓ amarra a venda a um contrato já vigente (sem negócio
//     de origem), sem criar nada — pra contrato que veio de outra fonte (import/Ploomes).
//
// KICKOFF (2026-08-20): virou wizard de 3 passos que TRAVA. O problema não era
// a tela, era o processo: a venda acontecia, o contrato era lançado e o kick-off
// nunca. Contrato nascia com gestor hardcoded (gustavo@), sem coordenador, e com
// todas as parcelas faturando no dia da venda. Agora:
//   passo 1 — vínculo + data de assinatura (sem pré-preenchimento)
//   passo 2 — gestor e coordenador OBRIGATÓRIOS + equipe técnica
//   passo 3 — prazo em dias de cada parcela, com a data resultante ao vivo
// O botão final só habilita com tudo preenchido, e o backend valida de novo —
// a tela é conveniência, o gate é o servidor (op_iniciar_contrato).
//
// 05/10/2026: quando a venda tem item com valor e SEM ÁREA, aparece um passo
// "Áreas" antes das parcelas. É dos itens da venda que sai a divisão por área do
// contrato; sem a área de um item, a receita dele não entra em área nenhuma. O
// que se escolhe aqui é gravado de volta na venda, como os prazos.
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import { Modal, Steps, Select, DatePicker, InputNumber, Alert, Table, Tag, Typography, Input, Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
import { carregarUsuariosOp } from '../operacional-contratos/utils';
import type { UsuarioOp } from '../operacional-contratos/types';
import { Mini2 } from './shared-ui';
import { listarAreas } from '../comercial-propostas/apiCatalogo';
import { AreaDoItem, AreasEmLote } from '../comercial-propostas/CelulaAreaItem';
import {
  aplicarEmLote, areaEfetivaDoItem, faltaNoItem, mapaDeServicos,
  type ItemVenda, type ServicoDoCatalogo,
} from '../comercial-propostas/areaDoItem';
import type { Subarea } from '../../utils/areasEmpresa';

const PRAZO_PAGAMENTO_DEFAULT = 30;

// Espelha _docsFonteDoNegocio + parcelas_detalhe do backend (op_preview_iniciar_contrato).
type ParcelaPreview = {
  documento_id: string;
  numero_proposta: string | null;
  indice: number;
  descricao: string;
  prazo_texto: string | null;
  valor_str: string | null;
  percentual: string | number | null;
  dias_apos_assinatura: number | string | null;
  prazo_pagamento_dias: number | string | null;
};
// Item da venda com valor e sem área (nem na linha, nem no serviço do catálogo).
type ItemSemArea = {
  documento_id: string; documento: string; indice: number; numero: number;
  descricao: string; valor: number; servico_id: string | null; cnae_servico: string | null;
};
// Linha de trabalho do passo "Áreas": o item como a régua da proposta o lê
// (areaDoItem.ts) e, se ele é de um serviço do catálogo, o serviço (sem área).
type LinhaArea = ItemSemArea & { item: ItemVenda; servico: ServicoDoCatalogo | null };

type Preview = {
  propostas_count: number; fonte: string;
  total_servicos: number; total_parcelas: number; total_valor: number;
  parcelas_detalhe?: ParcelaPreview[];
  itens_sem_area?: ItemSemArea[];
  // Área da venda e o coordenador que o backend sugere por causa dela. A regra
  // (quais áreas são "de campo" e quem coordena) mora em agentes.config, não
  // aqui — quem sabe o que conta como campo é o Lucas, e isso muda.
  area_negocio?: string | null;
  coordenador_sugerido?: string | null;
  propostas: Array<any>;
};

// Linha de trabalho do passo 3 — os dias viram string enquanto se digita.
type LinhaParcela = ParcelaPreview & { dias: number | null; pagto: number | null };

export type OpcoesKickoff = {
  contrato_pai_id?: string;
  motivo_aditivo?: string;
  vincular_origem_contrato_id?: string;
  gestor_email?: string;
  coordenador_email?: string;
  equipe_emails?: string[];
  parcelas_prazos?: Array<{ documento_id: string; indice: number;
    dias_apos_assinatura: number; prazo_pagamento_dias: number }>;
  // Área escolhida aqui para os itens que estavam sem — vai gravada na venda.
  itens_areas?: Array<{ documento_id: string; indice: number; descricao: string;
    setor_chave: string; subarea_id: string | null; cnae: string | null }>;
};

export function ModalIniciarContrato({ negocioId, clienteId, loading, onClose, onConfirmar }: {
  negocioId: number; clienteId?: number | null; loading: boolean;
  onClose: () => void;
  onConfirmar: (dataAssinatura: string, opts: OpcoesKickoff) => void;
}) {
  const [passo, setPasso] = useState(0);
  // Sem pré-preenchimento: a data define a emissão e o vencimento de TODAS as
  // parcelas. "Hoje" ali fazia o plano financeiro nascer vencendo em hoje+30
  // sem ninguém decidir isso.
  const [dataAssinatura, setDataAssinatura] = useState<Dayjs | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  // Prévia que não carregou NÃO é "venda sem parcelas e sem itens": sem ela a
  // tela não sabe o que falta, então não deixa avançar e oferece tentar de novo.
  const [erroPreview, setErroPreview] = useState(false);

  // Vínculo: contrato novo (default), aditivo de existente, ou vincular a existente.
  const [modo, setModo] = useState<'novo' | 'aditivo' | 'vincular_origem'>('novo');
  const [contratos, setContratos] = useState<any[]>([]);
  const [contratoPaiId, setContratoPaiId] = useState<string>('');
  const [motivo, setMotivo] = useState('');

  // Equipe
  const [usuarios, setUsuarios] = useState<UsuarioOp[]>([]);
  const [gestor, setGestor] = useState<string | undefined>(undefined);
  const [coordenador, setCoordenador] = useState<string | undefined>(undefined);
  const [equipe, setEquipe] = useState<string[]>([]);

  // Parcelas
  const [linhas, setLinhas] = useState<LinhaParcela[]>([]);

  // Áreas dos itens que chegaram sem
  const [itensArea, setItensArea] = useState<LinhaArea[]>([]);
  const [subareas, setSubareas] = useState<Subarea[] | null>(null);
  // Sub-áreas que não carregam NÃO são "área sem sub-área": o passo "Áreas"
  // mostra o erro com "Tentar de novo" e não avança sem elas.
  const [estadoAreas, setEstadoAreas] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  function carregarAreas() {
    setEstadoAreas('carregando');
    listarAreas()
      .then(areas => { setSubareas(areas.flatMap(a => a.subareas)); setEstadoAreas('ok'); })
      .catch(() => setEstadoAreas('erro'));
  }
  useEffect(() => { carregarAreas(); }, []);

  async function carregarPreview() {
    setLoadingPreview(true); setErroPreview(false);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_preview_iniciar_contrato', negocio_id: negocioId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (!d || !Array.isArray(d.propostas)) throw new Error('resposta inesperada');
      setPreview(d);
      setItensArea((Array.isArray(d.itens_sem_area) ? d.itens_sem_area : []).map((i: ItemSemArea) => ({
        ...i,
        item: { servico_id: i.servico_id, descricao: i.descricao, quantidade: 1, valor_unitario: i.valor },
        servico: i.servico_id ? { id: i.servico_id, setor_chave: null, subarea_id: null, cnae: i.cnae_servico } : null,
      })));
      // Pré-preenche o coordenador quando a venda é de campo. Só sugestão:
      // o passo 2 continua exigindo a escolha e o campo segue editável.
      if (d?.coordenador_sugerido) setCoordenador(d.coordenador_sugerido);
      setLinhas((d?.parcelas_detalhe || []).map((p: ParcelaPreview) => ({
        ...p,
        dias: p.dias_apos_assinatura === null || p.dias_apos_assinatura === undefined
          || String(p.dias_apos_assinatura).trim() === ''
          ? null : Number(p.dias_apos_assinatura),
        pagto: p.prazo_pagamento_dias === null || p.prazo_pagamento_dias === undefined
          || String(p.prazo_pagamento_dias).trim() === ''
          ? PRAZO_PAGAMENTO_DEFAULT : Number(p.prazo_pagamento_dias),
      })));
    } catch (e) { setPreview(null); setErroPreview(true); }
    finally { setLoadingPreview(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarPreview(); }, [negocioId]);

  useEffect(() => { carregarUsuariosOp().then(setUsuarios); }, []);

  // Contratos-base do mesmo cliente (pra escolher na opção de aditivo / vincular).
  useEffect(() => {
    if (!clienteId) return;
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_listar_contratos', cliente_id: clienteId },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setContratos((Array.isArray(d) ? d : []).filter((c: any) => !c.contrato_pai_id));
      } catch { /* silencioso */ }
    })();
  }, [clienteId]);

  const ehAditivo = modo === 'aditivo';
  const ehVincular = modo === 'vincular_origem';
  const contratosVincular = contratos.filter((c: any) => !c.negocio_id);
  const listaContratos = ehVincular ? contratosVincular : contratos;
  const contratoPai = contratos.find((c: any) => c.id === contratoPaiId);

  // Aditivo herda gestor/coordenador do pai quando não se escolhe outro — o
  // backend aplica a mesma regra, então a tela não pode exigir mais que ele.
  const gestorEfetivo = gestor || (ehAditivo ? contratoPai?.gestor_email : null) || null;
  const coordEfetivo = coordenador || (ehAditivo ? contratoPai?.coordenador_email : null) || null;

  const opcoesUsuarios = useMemo(() =>
    usuarios.map(u => ({ value: u.email, label: `${u.nome} (${u.email})` })), [usuarios]);

  const parcelasPendentes = linhas.filter(l => l.dias === null || l.dias === undefined).length;
  // Itens em que ainda falta escolher a área (ou a sub-área) — a mesma régua da
  // tela da proposta. O servidor só exige a área; a sub-área é da tela.
  const servicosDosItens = useMemo(
    () => mapaDeServicos(itensArea.map(i => i.servico).filter(Boolean) as ServicoDoCatalogo[]), [itensArea]);
  const areasPendentes = itensArea.filter(i => !!faltaNoItem(i.item, i.servico, subareas)).length;

  // ─── Travas por passo ────────────────────────────────────────────────────
  // Sem a prévia a tela não sabe quais parcelas e itens existem: não avança.
  const passo1Ok = ehVincular ? !!contratoPaiId
    : (!!preview && !!dataAssinatura && (!ehAditivo || !!contratoPaiId));
  const passo2Ok = ehVincular || (!!gestorEfetivo && !!coordEfetivo);
  const passo3Ok = ehVincular || parcelasPendentes === 0;
  const passoAreasOk = ehVincular || (estadoAreas === 'ok' && areasPendentes === 0);
  const podeConfirmar = passo1Ok && passo2Ok && passoAreasOk && passo3Ok;

  // O modo "vincular" não cria contrato: nem equipe, nem área, nem parcela fazem
  // sentido. O passo "Áreas" só existe quando algum item da venda chegou sem área.
  type Etapa = 'vinculo' | 'equipe' | 'areas' | 'parcelas';
  const etapas: Etapa[] = ehVincular ? ['vinculo']
    : ['vinculo', 'equipe', ...(itensArea.length ? ['areas' as Etapa] : []), 'parcelas'];
  const TITULO_ETAPA: Record<Etapa, string> = {
    vinculo: ehVincular ? 'Vínculo' : 'Vínculo e data', equipe: 'Equipe', areas: 'Áreas', parcelas: 'Parcelas',
  };
  const ETAPA_OK: Record<Etapa, boolean> = { vinculo: passo1Ok, equipe: passo2Ok, areas: passoAreasOk, parcelas: passo3Ok };
  const passos = etapas.map(e => ({ title: TITULO_ETAPA[e] }));
  const passoAtual = Math.min(passo, etapas.length - 1);
  const etapa = etapas[passoAtual];
  const ultimoPasso = passoAtual === etapas.length - 1;
  const passoAtualOk = ETAPA_OK[etapa];

  function confirmar() {
    if (ehVincular) return onConfirmar('', { vincular_origem_contrato_id: contratoPaiId });
    const iso = dataAssinatura ? dataAssinatura.format('YYYY-MM-DD') : '';
    onConfirmar(iso, {
      contrato_pai_id: ehAditivo ? contratoPaiId : undefined,
      motivo_aditivo: ehAditivo ? (motivo.trim() || undefined) : undefined,
      gestor_email: gestorEfetivo || undefined,
      coordenador_email: coordEfetivo || undefined,
      equipe_emails: equipe.length ? equipe : undefined,
      // Só manda o que foi de fato preenchido aqui — o backend grava de volta
      // no documento de venda, então mandar lixo sujaria a venda.
      parcelas_prazos: linhas
        .filter(l => l.dias !== null && l.dias !== undefined)
        .map(l => ({
          documento_id: l.documento_id, indice: l.indice,
          dias_apos_assinatura: Number(l.dias),
          prazo_pagamento_dias: Number(l.pagto ?? PRAZO_PAGAMENTO_DEFAULT),
        })),
      // A área escolhida para cada item que estava sem. A descrição vai junto
      // para o servidor conferir que a linha daquela posição ainda é a mesma.
      itens_areas: itensArea.flatMap(i => {
        const ef = areaEfetivaDoItem(i.item, i.servico);
        return ef.setor ? [{
          documento_id: i.documento_id, indice: i.indice, descricao: i.descricao,
          setor_chave: ef.setor, subarea_id: ef.subarea_id, cnae: ef.cnae,
        }] : [];
      }),
    });
  }

  function setItemArea(idx: number, novo: ItemVenda) {
    setItensArea(ls => ls.map((l, i) => (i === idx ? { ...l, item: novo } : l)));
  }

  function setLinha(idx: number, campo: 'dias' | 'pagto', v: number | null) {
    setLinhas(ls => ls.map((l, i) => (i === idx ? { ...l, [campo]: v } : l)));
  }

  const dataIso = dataAssinatura ? dataAssinatura.format('YYYY-MM-DD') : null;
  const somarDias = (base: string | null, n: number | null | undefined) => {
    if (!base || n === null || n === undefined || !Number.isFinite(n)) return null;
    return dayjs(base).add(n, 'day').format('YYYY-MM-DD');
  };

  return (
    <Modal
      open
      title="🚀 Kick-off do contrato"
      onCancel={onClose}
      maskClosable={!loading}
      width="94vw"
      style={{ maxWidth: 940, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' } }}
      // Rodapé próprio: "Voltar" tem que ser um botão à parte de "Cancelar" —
      // no antd o onCancel do Modal é o mesmo do X e do clique fora, então
      // reaproveitá-lo pra voltar faria o X andar um passo em vez de fechar.
      footer={[
        <Button key="cancelar" onClick={onClose} disabled={loading}>Cancelar</Button>,
        passoAtual > 0 ? (
          <Button key="voltar" onClick={() => setPasso(passoAtual - 1)} disabled={loading}>← Voltar</Button>
        ) : null,
        <Button key="ok" type="primary" loading={loading}
          disabled={loading || (ultimoPasso ? !podeConfirmar : !passoAtualOk)}
          onClick={() => (ultimoPasso ? confirmar() : setPasso(passoAtual + 1))}>
          {loading ? 'Processando…'
            : ehVincular ? '🔗 Vincular'
            : ultimoPasso ? (ehAditivo ? '➕ Confirmar aditivo' : '🚀 Confirmar e iniciar')
            : 'Avançar →'}
        </Button>,
      ]}
    >
      <Steps size="small" current={passoAtual} items={passos} style={{ marginBottom: 18 }} />

      {/* ─────────────────────── PASSO 1 — vínculo e data ─────────────────── */}
      {etapa === 'vinculo' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {ehVincular ? (
              <>Vincula esta venda como <strong>origem de um contrato já existente</strong> —
              não cria contrato, nem serviços, nem parcelas. Use quando o contrato veio
              de outra fonte (import/Ploomes) e está sem o negócio amarrado.</>
            ) : loadingPreview ? 'Calculando preview...' : erroPreview ? null : (
              <>
                {preview?.propostas_count || 0} {preview?.fonte === 'venda' ? 'documento(s) de venda' : 'proposta(s)'} ativa(s)
                {' '}serão unificadas em {ehAditivo ? <strong>um aditivo do contrato escolhido</strong> : <strong>1 contrato operacional</strong>}.
              </>
            )}
          </Typography.Text>

          {!ehVincular && (
            <div style={{ background: '#FAFBFF', border: '0.5px solid #E4E7F1',
              borderRadius: 6, padding: 14 }}>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase',
                letterSpacing: 0.5, marginBottom: 8 }}>Preview do que será criado</div>
              {loadingPreview ? (
                <div style={{ fontSize: 12, color: '#888', textAlign: 'center', padding: 16 }}>⏳ Carregando...</div>
              ) : erroPreview ? (
                <Alert type="error" showIcon
                  message="Não consegui carregar o que será criado"
                  description="Sem isso não dá para conferir as parcelas e os itens da venda. Nada foi criado."
                  action={<Button size="small" onClick={carregarPreview}>Tentar de novo</Button>} />
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    <Mini2 valor={String(preview?.total_servicos || 0)} label="serviço(s) (entregáveis)" cor="#0930D0" />
                    <Mini2 valor={String(preview?.total_parcelas || 0)} label="parcela(s) a receber" cor="#7C3AED" />
                    <Mini2 valor={`R$ ${(preview?.total_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
                      label="valor total" cor="#020D7F" />
                  </div>
                  {preview?.fonte === 'proposta' && (preview?.propostas_count || 0) > 0 && (
                    <Alert type="warning" showIcon style={{ marginTop: 10 }}
                      message="Ainda não há documento de venda"
                      description="Os números vêm das propostas comerciais ativas. Recomenda-se gerar o documento de venda antes (✓ Ganhar) pra refletir os valores finais editados." />
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <Rotulo>Vínculo</Rotulo>
            <Select style={{ width: '100%' }} value={modo}
              onChange={(v) => { setModo(v); setContratoPaiId(''); setPasso(0); }}
              options={[
                { value: 'novo', label: '🆕 Contrato novo' },
                { value: 'aditivo', label: '➕ Aditivo de contrato existente' },
                { value: 'vincular_origem', label: '🔗 Vincular a contrato existente (não cria nada)' },
              ]} />
          </div>

          {(ehAditivo || ehVincular) && (
            <div>
              <Rotulo>Contrato do cliente {ehAditivo ? '*' : '*'}</Rotulo>
              <Select style={{ width: '100%' }} value={contratoPaiId || undefined}
                onChange={setContratoPaiId} showSearch optionFilterProp="label"
                placeholder="— escolha o contrato do cliente —"
                options={listaContratos.map((c: any) => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))} />
              {!listaContratos.length && (
                <Typography.Text type="warning" style={{ fontSize: 11 }}>
                  {ehVincular
                    ? 'Nenhum contrato deste cliente sem negócio de origem. (Os que já têm venda vinculada não aparecem aqui.)'
                    : 'Nenhum contrato-pai cadastrado pra este cliente. Use "Contrato novo".'}
                </Typography.Text>
              )}
              {ehAditivo && (
                <Input style={{ marginTop: 8 }} value={motivo} onChange={e => setMotivo(e.target.value)}
                  placeholder="Motivo do aditivo (opcional)" />
              )}
            </div>
          )}

          {!ehVincular && (
            <div>
              <Rotulo>Data de assinatura do contrato *</Rotulo>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={dataAssinatura} onChange={setDataAssinatura}
                placeholder="Escolha a data" />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                É desta data que saem as parcelas: emissão da NF = assinatura + dias,
                vencimento = emissão + prazo de pagamento. Sem pré-preenchimento de propósito.
              </Typography.Text>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────── PASSO 2 — equipe ─────────────────────────── */}
      {etapa === 'equipe' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Alert type="info" showIcon
            message="Quem vai tocar este contrato?"
            description="Gestor e coordenador são avisados por e-mail assim que o contrato é criado, e só quem está na equipe consegue subir arquivo, marcar entregável e mexer na ART. Por isso os dois são obrigatórios." />

          <div>
            <Rotulo>Gestor do contrato *</Rotulo>
            <Select style={{ width: '100%' }} showSearch optionFilterProp="label" allowClear
              placeholder="— escolha o gestor —"
              value={gestor} onChange={setGestor} options={opcoesUsuarios} />
            {ehAditivo && !gestor && contratoPai?.gestor_email && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Herdando do contrato pai: <Tag>{contratoPai.gestor_email}</Tag>
              </Typography.Text>
            )}
          </div>

          <div>
            <Rotulo>Coordenador técnico *</Rotulo>
            <Select style={{ width: '100%' }} showSearch optionFilterProp="label" allowClear
              placeholder="— escolha o coordenador —"
              value={coordenador} onChange={setCoordenador} options={opcoesUsuarios} />
            {ehAditivo && !coordenador && contratoPai?.coordenador_email && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Herdando do contrato pai: <Tag>{contratoPai.coordenador_email}</Tag>
              </Typography.Text>
            )}
            {preview?.coordenador_sugerido && coordenador === preview.coordenador_sugerido && (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Sugerido porque a venda é de <strong>{preview.area_negocio}</strong> — troque
                se quem coordena este projeto for outra pessoa.
              </Typography.Text>
            )}
          </div>

          <div>
            <Rotulo>Equipe técnica (opcional)</Rotulo>
            <Select style={{ width: '100%' }} mode="multiple" showSearch optionFilterProp="label"
              placeholder="Quem mais trabalha neste contrato — dá pra completar depois"
              value={equipe} onChange={setEquipe} options={opcoesUsuarios} />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Pode ficar vazio agora: o gestor completa a equipe na aba Equipe do contrato.
            </Typography.Text>
          </div>

          {!passo2Ok && (
            <Alert type="error" showIcon
              message="Faltam gestor e/ou coordenador"
              description="Sem esses dois, o contrato não é criado." />
          )}
        </div>
      )}

      {/* ─────────────────────── PASSO — áreas dos itens ──────────────────── */}
      {etapa === 'areas' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {estadoAreas === 'erro' && (
            <Alert type="error" showIcon
              message="Não consegui carregar as sub-áreas"
              description="Sem elas não dá para saber se a área escolhida pede sub-área. Nada foi gravado."
              action={<Button size="small" onClick={carregarAreas}>Tentar de novo</Button>} />
          )}
          {estadoAreas === 'carregando' && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Carregando as sub-áreas…</Typography.Text>
          )}
          <Alert
            type={areasPendentes ? 'error' : 'success'} showIcon
            message={areasPendentes
              ? (areasPendentes === 1 ? '1 item da venda sem área' : `${areasPendentes} itens da venda sem área`)
              : 'Todos os itens têm área'}
            description={areasPendentes
              ? 'Cada item vendido entra na receita de uma área da empresa. Sem a área, o valor dele não aparece em área nenhuma do resultado. Escolha para continuar; o documento de venda é corrigido junto.'
              : 'É pelos itens da venda que o contrato sabe quanto é de cada área.'} />
          {areasPendentes > 1 && (
            <AreasEmLote quantas={areasPendentes} semContagem
              soArea={itensArea.every(i => faltaNoItem(i.item, i.servico, subareas) !== 'subarea')}
              subareas={subareas}
              onAplicar={(escolha) => {
                const novos = aplicarEmLote(itensArea.map(i => i.item), servicosDosItens, subareas, escolha);
                setItensArea(ls => ls.map((l, i) => ({ ...l, item: novos[i] })));
              }} />
          )}
          <Table<LinhaArea>
            size="small" pagination={false} rowKey={(r) => `${r.documento_id}:${r.indice}`}
            dataSource={itensArea}
            scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Item da venda', dataIndex: 'descricao',
                render: (v, r) => (<>
                  {v}
                  <div style={{ fontSize: 10, color: '#888' }}>{r.documento} · item {r.numero}</div>
                </>) },
              { title: 'Valor', width: 130, align: 'right',
                render: (_, r) => `R$ ${Number(r.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
              { title: 'Área', width: 280,
                render: (_, r, i) => (
                  <AreaDoItem item={r.item} servico={r.servico} subareas={subareas} numero={i + 1}
                    onChange={(novo) => setItemArea(i, novo)} />
                ) },
            ]} />
        </div>
      )}

      {/* ─────────────────────── PASSO — parcelas ─────────────────────────── */}
      {etapa === 'parcelas' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Alert
            type={parcelasPendentes ? 'error' : 'success'} showIcon
            message={parcelasPendentes
              ? `${parcelasPendentes} parcela(s) sem prazo em dias`
              : 'Todas as parcelas têm prazo'}
            description={parcelasPendentes
              ? 'Sem esse número a parcela nasce faturando no dia da assinatura — que é como todos os contratos nasceram até agora. Preencha para continuar; o documento de venda é corrigido junto.'
              : 'Emissão da NF = assinatura + dias. Vencimento = emissão + prazo de pagamento.'} />

          {!linhas.length ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Este negócio não tem parcelas no documento de venda — nada a preencher.
            </Typography.Text>
          ) : (
            <Table<LinhaParcela>
              size="small" pagination={false} rowKey={(r) => `${r.documento_id}:${r.indice}`}
              dataSource={linhas}
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Parcela', dataIndex: 'descricao', ellipsis: true,
                  render: (v, r) => (<>
                    {v}
                    {r.prazo_texto && <div style={{ fontSize: 10, color: '#888' }}>“{r.prazo_texto}”</div>}
                  </>) },
                { title: 'Valor', width: 110, align: 'right',
                  render: (_, r) => (r.valor_str
                    ? `R$ ${Number(r.valor_str).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                    : (r.percentual != null ? `${r.percentual}%` : '—')) },
                { title: 'Emitir + dias', width: 120, align: 'right',
                  render: (_, r, i) => (
                    <InputNumber style={{ width: '100%' }} min={0} step={1} precision={0}
                      value={r.dias} onChange={(v) => setLinha(i, 'dias', v as number | null)}
                      placeholder="—" status={r.dias === null ? 'error' : undefined} />
                  ) },
                { title: 'Pagto + dias', width: 120, align: 'right',
                  render: (_, r, i) => (
                    <InputNumber style={{ width: '100%' }} min={0} step={1} precision={0}
                      value={r.pagto} onChange={(v) => setLinha(i, 'pagto', v as number | null)}
                      placeholder={String(PRAZO_PAGAMENTO_DEFAULT)} />
                  ) },
                { title: 'Emissão', width: 110, align: 'right',
                  render: (_, r) => {
                    const e = somarDias(dataIso, r.dias);
                    return e ? fmtDataBr(e) : <span style={{ color: '#bbb' }}>—</span>;
                  } },
                { title: 'Vencimento', width: 110, align: 'right',
                  render: (_, r) => {
                    const e = somarDias(dataIso, r.dias);
                    const v = somarDias(e, r.pagto ?? PRAZO_PAGAMENTO_DEFAULT);
                    return v ? <strong style={{ color: '#020D7F' }}>{fmtDataBr(v)}</strong>
                      : <span style={{ color: '#bbb' }}>—</span>;
                  } },
              ]} />
          )}

          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Assinatura escolhida: <strong>{dataIso ? fmtDataBr(dataIso) : '—'}</strong>.
            O que você preencher aqui também corrige o documento de venda.
          </Typography.Text>
        </div>
      )}
    </Modal>
  );
}

function Rotulo({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>
      {children}
    </div>
  );
}
